import { buildStreamDependencies } from '@/app/pages/Settings/Role/role-dialog/lib/dashboardStreamCoverage'
import type { AllOrSpecific } from '@/rbac/types'
import type { SerializedTimeRange, TimeRange } from './../stores/timerange'
import { apiCall, apiCallWithBigInt, getTenantFromUrl } from './axiosInstance'
import type { Filters } from './deviceApi'

export type { AllOrSpecific }

export enum DashboardType {
	FleetDashboard = 'fleet',
	DeviceDashboard = 'device',
}

export type DashboardLayoutType = 'device' | 'fleet'

export interface DashboardMeta {
	title: string
	info: string
	type: DashboardType
	allowedFilterBys: string[]
	showMetadataKeys: string[]
	allowedGroupBys: string[]
	refreshInterval: number
	timeRange: SerializedTimeRange
	showTimeRange: boolean
	showRefreshInterval: boolean
	presetFilters?: Filters
	dashboardLinks?: string[]
	hideFromAdmins?: boolean
}

export type GridLayoutItem = {
	i: string
	x: number
	y: number
	w: number
	h: number
	minW?: number
	minH?: number
	maxW?: number
	maxH?: number
	static?: boolean
}

export interface GridLayout {
	lg: GridLayoutItem[]
	xs: GridLayoutItem[]
	[key: string]: GridLayoutItem[]
}

export interface DashboardConfig {
	dashboardMeta: DashboardMeta
	panels?: PanelMetaData[]
	gridLayouts?: GridLayout
	timeRange?: SerializedTimeRange
	/** Streams + fields the panels read, recomputed on every save — fixed location for backend dependency tracking. */
	streamDependencies?: { name: string; fields: string[] }[]
}

export interface Dashboard {
	id: number
	config: DashboardConfig
	created_by?: string
	timeRange?: TimeRange
}

export type PanelMetaData = {
	type: string
	id: string
	description: string
	title: string
	table?: string
	columns?: string[]
	query?: unknown
	timeseries?: unknown
	groupBys?: string[]
	alert_rules?: string[]
	page?: number
	sortOrder?: string
	rowsPerPage?: number
}
export type FetchParams = {
	groupBys: string[]
	filterBys: { [key: string]: string[] }
	timeRange: TimeRange
	fetchAll: boolean
}

export interface PanelDataRequest {
	dashboard_id: string
	panel_id: string
	startTime: number
	endTime: number
	aggregationInterval: number
	filterBys: Record<string, unknown>
	groupBys: string[]
	panels: PanelMetaData[]
	fetchAll: boolean
}

export interface PanelDataResponse {
	data: unknown
}

export interface CreateDashboardResponse {
	id: number
	status: string
}

export interface DashboardShareOptionsResponse {
	roles: string[]
	users: { name: string; email: string }[]
}

export interface DashboardHistoryLog {
	id: string
	user_email: string
	dashboard_id: number
	timestamp: string
	name: string | null
	event: string
	new_json: DashboardConfig
}

export interface DashboardHistoryResponse {
	data: DashboardHistoryLog[]
}

export interface DashboardRolePermissions {
	'dashboard:read': AllOrSpecific<number[]>
	'dashboard:update': AllOrSpecific<number[]>
	'dashboard:delete': AllOrSpecific<number[]>
}

export type ShareDashboardPayload = Record<string, DashboardRolePermissions>

export interface DashboardListItem {
	id: number
	name: string | null
	/** Top-level `config.dashboardMeta.type`, so the list can be read without fetching configs. */
	type?: string | null
	created_by?: string
	config?: DashboardConfig
}

export interface DashboardListResponse {
	count: number
	data: DashboardListItem[]
}

export interface DashboardResponse {
	id: number
	name: string | null
	created_by?: string
	config: DashboardConfig
}

// Device search filter types matching backend Rust struct
/**
 * `timestamp with time zone` / `date` columns (e.g. device_state `heartbeat`) are type-checked
 * against `PrimitiveValue::Timestamp` / `::Date` server-side, so their values must be sent wrapped —
 * a bare ISO string deserializes as `PrimitiveValue::String` and is rejected.
 */
export interface SearchTimestampValue {
	timestamp: string // RFC3339
}
export interface SearchDateValue {
	date: string // YYYY-MM-DD
}
export type SearchValue = string | number | boolean | SearchTimestampValue | SearchDateValue

// SearchOp for id filter (no column needed)
export interface SearchOp {
	eq?: SearchValue // equals
	ne?: SearchValue // not equals
	gt?: SearchValue // greater than
	gte?: SearchValue // greater than or equal
	lt?: SearchValue // less than
	lte?: SearchValue // less than or equal
	in?: SearchValue[] // in array
	regex?: string // regex match
	is_null?: boolean // snake_case for device filters
}

// SearchCondition for metadata/state filters (column + op)
export interface SearchCondition {
	column: string
	eq?: SearchValue
	ne?: SearchValue
	gt?: SearchValue
	gte?: SearchValue
	lt?: SearchValue
	lte?: SearchValue
	in?: SearchValue[]
	regex?: string
	is_null?: boolean
}

// DeviceSearchFilter matches backend enum:
// - and/or: array of filters
// - not: single filter
// - metadata/state: SearchCondition with column
// - id: SearchOp directly (no column)
export interface DeviceSearchFilter {
	and?: DeviceSearchFilter[]
	or?: DeviceSearchFilter[]
	not?: DeviceSearchFilter
	metadata?: SearchCondition
	state?: SearchCondition
	id?: SearchOp
}

// Data filter types for timeseries panel
export interface DataFilter {
	and?: DataFilter[]
	or?: DataFilter[]
	not?: DataFilter
	condition?: DataFilterCondition
}

export interface DataFilterCondition {
	column: string
	eq?: string | number | boolean
	ne?: string | number | boolean
	gt?: string | number
	gte?: string | number
	lt?: string | number
	lte?: string | number
	in?: (string | number | boolean)[]
	regex?: string
	iregex?: string // case-insensitive regex
	ansiregex?: string // lowercase (no underscore) for dashboard filters
	isnull?: boolean // lowercase (no underscore) for dashboard filters
}

// Timeseries Table API types
export interface TimeseriesTableRequest {
	stream: string
	fields: string[]
	start: string // ISO 8601 timestamp (RFC3339)
	end: string // ISO 8601 timestamp (RFC3339)
	device_filters?: DeviceSearchFilter
	data_filters?: DataFilter
	page: number // 1-1000000
	per_page: number // 1-1000
	generate_histogram?: boolean
	sort_order?: 'asc' | 'desc'
}

export interface TimeseriesTableResponseItem {
	id: string
	timestamp: string
	insert_timestamp: string
	[key: string]: unknown
}

export interface HistogramBucket {
	start: string
	end: string
	height: number
}

export interface TimeseriesTableResponse {
	data: TimeseriesTableResponseItem[]
	count: number
	histogram?: HistogramBucket[]
}

// Timeseries Download API types
export interface TimeseriesDownloadRequest {
	stream: string
	fields: string[]
	start: string // ISO 8601 timestamp (RFC3339)
	end: string // ISO 8601 timestamp (RFC3339)
	timezone_offset?: number // offset in seconds
	device_filters?: DeviceSearchFilter
	data_filters?: DataFilter
	sort_order?: 'asc' | 'desc'
}

// Timeseries Live Stream API types
export interface TimeseriesLiveStreamParams {
	stream: string
	fields: string[]
	device_filters?: DeviceSearchFilter
	data_filters?: DataFilter
}

export const dashboardApi = {
	getDashboards: async (page = 1, perPage = 100, fetchConfig = false): Promise<DashboardListResponse> =>
		apiCall(`/v3/dashboards?page=${page}&per_page=${perPage}&fetch_config=${fetchConfig}`),

	getFullDashboardData: async (dashboardId: number): Promise<DashboardResponse> =>
		apiCall(`/v3/dashboards/${dashboardId}`),
	getDashboardShareOptions: async (): Promise<DashboardShareOptionsResponse> => apiCall('/v3/dashboard-share-options'),

	createDashboard: async (dashboardMeta: DashboardMeta, config?: DashboardConfig): Promise<CreateDashboardResponse> => {
		const resolvedConfig = config ?? { dashboardMeta, panels: [] }
		return apiCall('/v3/dashboards', {
			method: 'POST',
			data: {
				config: { ...resolvedConfig, streamDependencies: buildStreamDependencies(resolvedConfig) },
			},
		})
	},

	deleteDashboard: async (dashboardId: number): Promise<void> =>
		apiCall(`/v3/dashboards/${dashboardId}`, { method: 'DELETE' }),

	updateDashboardConfig: async (dashboardId: number, config: DashboardConfig): Promise<Dashboard> =>
		apiCallWithBigInt(`/v3/dashboards/${dashboardId}`, {
			method: 'PUT',
			data: {
				name: config.dashboardMeta.title,
				config: { ...config, streamDependencies: buildStreamDependencies(config) },
			},
		}),

	shareDashboard: async (dashboardId: number, rolesPayload: ShareDashboardPayload): Promise<void> =>
		apiCall(`/v3/dashboards/${dashboardId}/share`, {
			method: 'POST',
			data: rolesPayload,
		}),

	fetchPanelData: async (requestBody: PanelDataRequest): Promise<PanelDataResponse> =>
		apiCallWithBigInt('/v3/panel-data', { method: 'POST', data: requestBody }),

	getDashboardHistory: async (dashboardId: number): Promise<DashboardHistoryResponse> =>
		apiCall(`/v3/dashboards/${dashboardId}/logs`),

	renameDashboardHistoryLog: async (_dashboardId: number, version: string, name: string): Promise<void> =>
		apiCall(`/v3/dashboard-logs/${version}`, {
			method: 'PUT',
			data: { name },
		}),

	rollbackDashboard: async (dashboardId: number, version: string): Promise<void> =>
		apiCall(`/v3/dashboards/${dashboardId}/rollback/${version}`, {
			method: 'POST',
		}),

	fetchAllLogTags: async (table: string): Promise<{ tags: string[] }> =>
		apiCall(`/v3/log-tags?table=${encodeURIComponent(table)}`),

	// Returns paginated timeseries data for a given stream and time range, with optional filters and grouping
	fetchTimeseriesTable: async (
		request: TimeseriesTableRequest,
		signal?: AbortSignal,
		tenantId?: string,
	): Promise<TimeseriesTableResponse> =>
		apiCallWithBigInt('/v3/timeseries', {
			method: 'POST',
			data: request,
			signal,
			...(tenantId && { headers: { 'x-bytebeam-tenant': tenantId, 'x-bytebeam-impersonate': 'admin' } }),
		}),

	// Timeseries Download API - returns CSV file stream
	downloadTimeseriesTable: async (request: TimeseriesDownloadRequest): Promise<Response> => {
		const tenant = getTenantFromUrl()
		return fetch('/api/v3/timeseries-download', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'x-bytebeam-tenant': tenant,
			},
			body: JSON.stringify(request),
			credentials: 'include',
		})
	},

	// Timeseries Live Stream API - returns EventSource URL
	// The frontend will connect to this URL using EventSource for real-time updates used in the live stream panel to fetch new data as it arrives without polling
	getTimeseriesLiveStreamUrl: (params: TimeseriesLiveStreamParams): string => {
		const tenant = (window as unknown as { __TENANT__?: string }).__TENANT__ || ''
		const encodedParams = encodeURIComponent(JSON.stringify(params))
		return `/api/v3/timeseries-tail?${encodedParams}&tenant=${tenant}`
	},
}
